import { useMsal } from '@azure/msal-react';
import { ArrowDropDown, Menu as MenuIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import React, { FC, useCallback, useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMedia } from 'react-use';

import { screenSizes } from '../../constants';
import { UserContext } from '../../context';
import { navLinkHeaders, userLogout, getHeaderColor, getHeaderTextColor, getEnvironmentLabel } from '../../helpers';
import { useMaterialMenu } from '../../hooks';
import { INavLink, INavLinkHeader } from '../../models';
import { colors } from '../../styles';
import { Logo } from './Logo';
import { HeaderContext } from '../../context';

export const Header: FC<{ title: string }> = ({ title }) => {
  const { instance } = useMsal();
  const { userContext } = useContext(UserContext);
  const { setHeaderHeight } = useContext(HeaderContext);
  const isMobile = useMedia(screenSizes.mobile);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [headerColor, setHeaderColor] = useState<string>(colors.olive);
  const [headerTextColor, setHeaderTextColor] = useState<string>('#ffffff');
  const [environmentLabel, setEnvironmentLabel] = useState<string | null>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    // Get the environment-based header color
    setHeaderColor(getHeaderColor());
    setHeaderTextColor(getHeaderTextColor());
    setEnvironmentLabel(getEnvironmentLabel());
  }, []);

  const closeUserMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const openUserMenu = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current !== null) {
      setHeaderHeight(ref.current.offsetHeight);
    }
  }, [ref.current?.offsetHeight, setHeaderHeight]);

  return (
    <div ref={ref}>
      {/* USER CONTROLS BAR */}
      {isMobile ? null : (
        <Box
          display="flex"
          flexDirection="row"
          height="100%"
          width="100%"
          alignItems="center"
          sx={{ backgroundColor: headerColor }}
        >
          {/** ENVIRONMENT LABEL */}
          {environmentLabel && (
            <Box sx={{ marginLeft: theme => theme.spacing(1) }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'rgb(90, 103, 96)',
                }}
              >
                {environmentLabel}
              </Typography>
            </Box>
          )}

          {/* USER CONTROLS */}
          <Box
            sx={theme => ({
              marginLeft: 'auto',
              // Space away from right wall
              marginRight: theme.spacing(1),

              // Spacing between buttons
              '& > *:not(:last-child)': {
                marginRight: theme.spacing(0.75),
              },
            })}
          >
            {/** NAV LINK DROPDOWNS */}
            {navLinkHeaders.map(nlh => (
              <NavLinkHeader navLinkHeader={nlh} key={nlh.name} />
            ))}

            <Button
              size="small"
              id="user-button"
              onClick={openUserMenu}
              endIcon={
                <ArrowDropDown
                  fontSize="small"
                  sx={{ color: headerTextColor }}
                />
              }
            >
              <Box margin={theme => theme.spacing(0, 0.4, -0.05, 0)}>
                <Typography sx={{ color: headerTextColor }} variant="subtitle2">
                  Logged In:
                </Typography>
              </Box>
              <Typography variant="subtitle2" sx={{ color: headerTextColor }}>
                {userContext?.fullName}
              </Typography>
            </Button>
          </Box>
        </Box>
      )}

      {/* USER CONTROL MENU */}
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeUserMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={theme => ({
          '.MuiPaper-root': {
            right: '0',
            left: 'auto !important',
            top: '65px !important',
            [theme.breakpoints.up(768)]: {
              top: '34px !important',
            },
            minWidth: '12.5rem',
            borderBottom: '4px solid',
            borderColor: colors.blueDark,
          },
        })}
      >
        {isMobile && environmentLabel && (
          <MenuItem disabled>
            <Typography variant="subtitle2" sx={{ color: 'rgb(90, 103, 96)' }}>
              {environmentLabel}
            </Typography>
          </MenuItem>
        )}

        {isMobile
          ? /** NAV LINK DROPDOWNS */
            navLinkHeaders.map(nlh => (
              <MenuItem key={`menuitem ${nlh.name}`}>
                <NavLinkHeader navLinkHeader={nlh} key={nlh.name} />
              </MenuItem>
            ))
          : null}

        <MenuItem
          onClick={() => {
            closeUserMenu();

            userLogout();
            instance.logoutRedirect();
          }}
          sx={theme => ({ color: colors.searchBlue })}
        >
          Logout
        </MenuItem>
      </Menu>

      <Box
        display="flex"
        alignItems="center"
        width="100%"
        padding={theme => theme.spacing(0.75)}
        borderTop={`1px solid ${colors.grayMid}`}
        borderBottom={`1px solid ${colors.grayMid}`}
      >
        <Logo />

        <Divider
          orientation="vertical"
          flexItem={true}
          variant="middle"
          sx={theme => ({
            // Space between logo and page title.
            marginRight: theme.spacing(1),
            marginLeft: theme.spacing(1),
            marginTop: '4px',
            marginBottom: '4px',
            borderColor: 'rgb(0 0 0 / .2)',
          })}
        />

        <Box width="100%">
          <Typography
            component="h1"
            sx={theme => ({
              // Nudge left.
              marginRight: '208px',
              // Taken from Frankie.
              lineHeight: '38.4px',
              fontFamily: 'OpenSansRegular, sans-serif',
              color: theme.frankie.searchBlue,

              // Keeps the header title centered as the page size decreaes
              [theme.breakpoints.down('lg')]: {
                marginRight: '100px',
              },
              [theme.breakpoints.down('sm')]: {
                fontSize: '28px',
              },

              fontSize: '24px',
            })}
          >
            {title}
          </Typography>
        </Box>
        {isMobile && (
          <Box display="flex">
            <Box marginLeft="auto">
              <IconButton color="primary" onClick={openUserMenu}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
};

const NavLinkHeader: FC<{ navLinkHeader: INavLinkHeader }> = ({ navLinkHeader }) => {
  const { openMenu, ...linksMenu } = useMaterialMenu();
  const isMobile = useMedia(screenSizes.mobile);
  const { userContext } = useContext(UserContext);
  const theme = useTheme();

  const [expanded, setExpanded] = useState(false);
  const headerTextColor = getHeaderTextColor();

  if (!userContext) {
    return null;
  }

  // Render nothing if no child links exist
  if (navLinkHeader.links.length === 0) {
    return null;
  }

  // If there is not one dropdown item the user can click, render nothing
  if (!navLinkHeader.links.flatMap(_ => _.roles).some(role => userContext.roles.has(role))) {
    return null;
  }

  // Ensure every required feature flag is present otherwise hide this dropdown nav item
  let filteredLinks = navLinkHeader.links.filter(link => {
    return link.requiredFeatureFlags.every(requiredFlagName =>
      userContext.featureFlags.some(
        flag => flag.name === requiredFlagName && flag.hasAccess === true
      )
    );
  });

  // Filter down to links where a user has a role with access
  filteredLinks = filteredLinks.filter(link => {
    return link.roles.some(role => userContext.roles.has(role));
  });

  // If no links remain after filtering, render nothing
  if(filteredLinks.length === 0){
    return null;
  }

  return (
    <>
      <Button
        startIcon={isMobile ? null : navLinkHeader.icon}
        endIcon={<ArrowDropDown sx={theme => ({ marginLeft: theme.spacing(-0.5) })} />}
        disableRipple
        onClick={event => {
          openMenu(event);
          setExpanded(true);
        }}
        sx={theme => ({
          margin: 0,
          padding: isMobile ? 0 : theme.spacing(0.25),
          color: isMobile ? colors.searchBlue : headerTextColor,
          fontSize: isMobile ? '16px' : '14px',
        })}
      >
        {navLinkHeader.name}
      </Button>

      {expanded && (
        <Menu {...linksMenu} MenuListProps={{ dense: true }}>
          {filteredLinks.map((link: INavLink) => (
            <MenuItem
              key={typeof link.route === 'function' ? link.name : link.route}
              sx={theme => ({
                // Based on Frankie dropdown nav menu width
                minWidth: '180px',
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: theme.frankie.primary,
                  '& *': {
                    color: 'white !important',
                  },
                },
              })}
            >
              <Link
                to={typeof link.route === 'function' ? link.route('') : link.route}
                style={{ textDecoration: 'none', color: theme.palette.text.primary }}
              >
                {link.name}
              </Link>
            </MenuItem>
          ))}
        </Menu>
      )}

    </>
  );
};
